import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import Modal from './Modal';
import GuideModal from './GuideModal';
import AudioVisualization from './AudioVisualization';
import ConcentrationGrid from './ConcentrationGrid';
import ProcessWizard from './ProcessWizard';
import API_URL from '../config';
import axios from 'axios';
import logger from '../utils/logger';
import StandardWidthContainer from './StandardWidthContainer';
import './DesktopComponentStyles.css';

const Mindset = forwardRef((props, ref) => {
  const [mindset, setMindset] = useState({
    oneWordFocus: '',
    keysToKeepItSimple: [],
    affirmationsAndBeliefs: [], 
    selfTalk: [],
    bodyLanguage: []
  });

  const [editing, setEditing] = useState({
    oneWordFocus: false,
    keysToKeepItSimple: false,
    affirmationsAndBeliefs: false, 
    selfTalk: false,
    bodyLanguage: false
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showVisualizationAudio, setShowVisualizationAudio] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [processWizardOpen, setProcessWizardOpen] = useState(false);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleProcessWizardComplete: (processData) => {
      handleProcessWizardComplete(processData);
    },
    refreshData: () => {
      fetchMindset();
    }
  }));

  useEffect(() => {
    fetchMindset();
  }, []);

  const fetchMindset = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Check if we have data in localStorage first
      let localData = null;
      try {
        const storedData = localStorage.getItem('mindsetData');
        const hasContentFlag = localStorage.getItem('mindsetDataHasContent');
        
        if (storedData) {
          localData = JSON.parse(storedData);
          logger.debug('Found mindset data in localStorage:', localData);
          
          // Check if localStorage data has any content or the content flag is set
          const hasContent = hasContentFlag === 'true' || 
                            localData.oneWordFocus || 
                            (localData.keysToKeepItSimple && localData.keysToKeepItSimple.length > 0) ||
                            (localData.affirmations && localData.affirmations.length > 0) ||
                            (localData.selfTalk && localData.selfTalk.length > 0) ||
                            (localData.bodyLanguage && localData.bodyLanguage.length > 0);
          
          if (hasContent) {
            logger.debug('Using mindset data from localStorage because it has content');
            
            // Convert the data structure if needed
            const mindsetData = {
              oneWordFocus: localData.oneWordFocus || '',
              keysToKeepItSimple: localData.keysToKeepItSimple || [],
              affirmationsAndBeliefs: [
                ...(localData.affirmations || []),
              ],
              selfTalk: localData.selfTalk || [],
              bodyLanguage: localData.bodyLanguage || []
            };
            
            setMindset(mindsetData);
            logger.debug('Set mindset data from localStorage:', mindsetData);
            setLoading(false);
            
            // Try to save to backend if we have content
            if (hasContent) {
              logger.debug('Attempting to save localStorage data to backend');
              
              // Extract the data needed for the backend
              const processData = {
                oneWordFocus: localData.oneWordFocus || '',
                keysToKeepItSimple: localData.keysToKeepItSimple || [],
                affirmations: localData.affirmations || [],
                selfTalk: localData.selfTalk || [],
                bodyLanguage: localData.bodyLanguage || []
              };
              
              // Save to backend
              saveProcessDataToBackend(processData);
            }
            
            // Still try to fetch from API in the background, but don't update UI
            fetchFromApi(false);
            return;
          }
        }
      } catch (e) {
        logger.error('Error parsing localStorage data:', e);
      }
      
      // If we don't have usable localStorage data, fetch from API and update UI
      await fetchFromApi(true);
      
    } catch (error) {
      logger.error('Error in fetchMindset:', error);
      setError('Failed to load mindset data. ' + error.message);
      setLoading(false);
    }
  };
  
  // Helper function to fetch from API
  const fetchFromApi = async (updateUi = true) => {
    try {
      // Get token for API calls
      const token = localStorage.getItem('token');
      if (!token) {
        if (updateUi) {
          setError('Authentication token missing. Please log in again.');
          setLoading(false);
        }
        return;
      }
      
      // Check if localStorage has the content flag set before making API calls
      const localStorageHasContent = localStorage.getItem('mindsetDataHasContent') === 'true';
      if (updateUi && localStorageHasContent) {
        logger.debug('Not fetching from API because localStorage has content flag set');
        setLoading(false);
        return;
      }
      
      // Try to fetch from API
      try {
        // Fetch mindset data
        const response = await axios.get(`${API_URL}/api/mindset`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        logger.debug('Mindset data fetched successfully from API:', response.data);
        
        // Check if API data has any content - check ALL fields
        const apiHasContent = response.data.oneWordFocus || 
                           (response.data.keysToKeepItSimple && response.data.keysToKeepItSimple.length > 0) ||
                           (response.data.affirmations && response.data.affirmations.length > 0) ||
                           (response.data.selfTalk && response.data.selfTalk.length > 0) ||
                           (response.data.bodyLanguage && response.data.bodyLanguage.length > 0) ||
                           (response.data.beliefs && response.data.beliefs.length > 0);
        
        // Only update state with API data if requested AND if API has content
        if (updateUi && apiHasContent) {
          logger.debug('Using data from API because it has content');
          
          const apiData = {
            oneWordFocus: response.data.oneWordFocus || '',
            keysToKeepItSimple: response.data.keysToKeepItSimple || [],
            affirmationsAndBeliefs: [
              ...(response.data.affirmations || []),
              ...(response.data.beliefs || [])
            ],
            selfTalk: response.data.selfTalk || [],
            bodyLanguage: response.data.bodyLanguage || []
          };
          
          logger.debug('Setting mindset state with API data:', apiData);
          setMindset(apiData);
          
          // Also update localStorage to ensure consistency
          localStorage.setItem('mindsetData', JSON.stringify(response.data));
          localStorage.setItem('mindsetDataHasContent', 'true');
          logger.debug('Updated localStorage with API data');
        } else if (updateUi && !apiHasContent) {
          logger.debug('API data is empty, using empty data structure');
          // If API data is empty, use an empty data structure
          const emptyData = {
            oneWordFocus: '',
            keysToKeepItSimple: [],
            affirmationsAndBeliefs: [],
            selfTalk: [],
            bodyLanguage: []
          };
          setMindset(emptyData);
        }
      } catch (apiError) {
        logger.error('Error fetching from API:', apiError);
        // Only set error if we're updating the UI
        if (updateUi) {
          setError('Failed to fetch from API. ' + apiError.message);
        }
      }
      
      if (updateUi) {
        setLoading(false);
      }
    } catch (error) {
      logger.error('Error in fetchFromApi:', error);
      if (updateUi) {
        setError('Failed to fetch from API. ' + error.message);
        setLoading(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setMindset(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setMindset(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleAdd = (field) => {
    setMindset(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemove = (field, index) => {
    setMindset(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const saveMindset = async () => {
    setSaveInProgress(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token missing. Please log in again.');
        setSaveInProgress(false);
        return;
      }

      // Prepare data for API
      // Split affirmationsAndBeliefs back into separate arrays for API
      // Instead of arbitrarily splitting in half, we'll maintain any previous separation
      // by checking localStorage for the previous state
      let affirmations = [];
      let beliefs = [];
      
      try {
        const storedData = localStorage.getItem('mindsetData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          
          // Get the previous affirmations and beliefs
          const prevAffirmations = parsedData.affirmations || [];
          
          // Any item that was previously in affirmations stays there,
          // new items go to affirmations by default
          affirmations = mindset.affirmationsAndBeliefs.filter(item => 
            prevAffirmations.includes(item) || 
            !parsedData.beliefs?.includes(item)
          );
          
          // Items that were previously in beliefs stay there
          beliefs = mindset.affirmationsAndBeliefs.filter(item => 
            parsedData.beliefs?.includes(item) && 
            !prevAffirmations.includes(item)
          );
        } else {
          // If no previous data, put all items in affirmations
          affirmations = mindset.affirmationsAndBeliefs;
          beliefs = [];
        }
      } catch (e) {
        logger.error('Error parsing localStorage for affirmations/beliefs split:', e);
        // Fallback to simple split
        affirmations = mindset.affirmationsAndBeliefs;
        beliefs = [];
      }
      
      const mindsetData = {
        oneWordFocus: mindset.oneWordFocus,
        keysToKeepItSimple: mindset.keysToKeepItSimple,
        affirmations,
        beliefs,
        selfTalk: mindset.selfTalk,
        bodyLanguage: mindset.bodyLanguage
      };

      // Save to local storage first as a backup
      localStorage.setItem('mindsetData', JSON.stringify(mindsetData));

      // Save mindset data
      logger.debug('Sending mindset data to API:', JSON.stringify(mindsetData, null, 2));
      logger.debug('API URL being used:', `${API_URL}/api/mindset`);
      logger.debug('Token first 20 chars:', token.substring(0, 20) + '...');
      
      try {
        const mindsetResponse = await axios.put(`${API_URL}/api/mindset`, mindsetData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        logger.debug('Mindset data save response status:', mindsetResponse.status);
        logger.debug('Mindset data save response:', JSON.stringify(mindsetResponse.data, null, 2));
      } catch (mindsetError) {
        logger.error('Error saving mindset data:', mindsetError.message);
        if (mindsetError.response) {
          logger.error('Error response status:', mindsetError.response.status);
          logger.error('Error response data:', JSON.stringify(mindsetError.response.data, null, 2));
        }
        // Continue with process data save even if mindset save fails
      }
      
      // Reset editing state
      setEditing({
        oneWordFocus: false,
        keysToKeepItSimple: false,
        affirmationsAndBeliefs: false,
        selfTalk: false,
        bodyLanguage: false
      });

      setMessage('Mindset saved successfully!');
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (error) {
      logger.error('Error saving mindset data:', error);
      setError('Failed to save mindset data. ' + (error.response?.data?.message || error.message));
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleProcessWizardComplete = async (processData) => {
    try {
      setSaveInProgress(true);
      setError('');
      
      logger.debug('Process Wizard data received:', JSON.stringify(processData, null, 2));
      
      // Immediately update the local state first to show the user their data
      const updatedMindset = {
        oneWordFocus: processData.oneWordFocus || mindset.oneWordFocus || '',
        affirmationsAndBeliefs: [...(processData.affirmations || []), ...(processData.beliefs || []), ...(mindset.affirmationsAndBeliefs || [])].filter(item => item && item.trim() !== ''),
        selfTalk: [...(processData.selfTalk || []), ...(mindset.selfTalk || [])].filter(item => item && item.trim() !== ''),
        bodyLanguage: [...(processData.bodyLanguage || []), ...(mindset.bodyLanguage || [])].filter(item => item && item.trim() !== ''),
        keysToKeepItSimple: [...(processData.keysToKeepItSimple || []), ...(mindset.keysToKeepItSimple || [])].filter(item => item && item.trim() !== '')
      };
      
      logger.debug('Updating local state with:', JSON.stringify(updatedMindset, null, 2));
      setMindset(updatedMindset);
      
      // Save to local storage as the primary data store
      const localStorageData = {
        oneWordFocus: processData.oneWordFocus || mindset.oneWordFocus || '',
        keysToKeepItSimple: [...(processData.keysToKeepItSimple || []), ...(mindset.keysToKeepItSimple || [])].filter(item => item && item.trim() !== ''),
        affirmations: processData.affirmations || [],
        beliefs: processData.beliefs || [],
        selfTalk: processData.selfTalk || [],
        bodyLanguage: processData.bodyLanguage || []
      };
      
      // Make sure to set a flag in localStorage to indicate this data has content
      localStorage.setItem('mindsetData', JSON.stringify(localStorageData));
      localStorage.setItem('mindsetDataHasContent', 'true');
      logger.debug('Saved to localStorage successfully with content flag');
      
      setMessage('Mindset data saved successfully!');
      
      // Try to save to the backend in the background without affecting the UI
      saveProcessDataToBackend(processData);
      
    } catch (error) {
      logger.error('Error in Process Wizard completion:', error);
      setError('Failed to process mindset data: ' + error.message);
    } finally {
      setSaveInProgress(false);
    }
  };
  
  // Separate function to handle backend API calls
  const saveProcessDataToBackend = async (processData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.debug('No token available for API calls');
        return;
      }
      
      logger.debug('Attempting to save Process Wizard data to backend...');
      
      // First, save the main mindset data
      const mindsetData = {
        oneWordFocus: processData.oneWordFocus || '',
        keysToKeepItSimple: processData.keysToKeepItSimple || [],
        affirmations: processData.affirmations || [],
        beliefs: processData.beliefs || [], // Include beliefs if available
        selfTalk: processData.selfTalk || [],
        bodyLanguage: processData.bodyLanguage || []
      };
      
      logger.debug('Sending mindset data to API:', JSON.stringify(mindsetData, null, 2));
      
      try {
        // Save mindset data
        const mindsetResponse = await axios.put(`${API_URL}/api/mindset`, mindsetData, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
        
        logger.debug('Mindset API save response:', mindsetResponse.status, mindsetResponse.data);
        
        // Verify the data was saved by checking the response
        if (mindsetResponse.data && mindsetResponse.data.mindset) {
          logger.debug('Saved mindset data successfully:', JSON.stringify(mindsetResponse.data.mindset, null, 2));
        } else {
          logger.warn('Mindset API response missing expected data structure');
        }

        // Also update the MindsetProcess model with the same bodyLanguage data
        // This ensures both models stay in sync
        try {
          logger.debug('Updating MindsetProcess with bodyLanguage data');
          const processResponse = await axios.put(`${API_URL}/api/mindset/process`, {
            bodyLanguage: processData.bodyLanguage || []
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          logger.debug('MindsetProcess update response:', processResponse.status);
        } catch (processError) {
          logger.error('Error updating MindsetProcess:', processError);
          // Continue even if this fails, as the main mindset data is already saved
        }
      } catch (mindsetError) {
        logger.error('Error saving mindset data:', mindsetError);
        console.error('Error details:', mindsetError.response ? {
          status: mindsetError.response.status,
          data: mindsetError.response.data,
          headers: mindsetError.response.headers
        } : 'No response data available');
        
        // Try fallback to profile endpoint
        try {
          logger.debug('Attempting fallback save to profile endpoint...');
          const profileResponse = await axios.put(`${API_URL}/api/users/profile`, { mindset: mindsetData }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          logger.debug('Profile fallback save response:', profileResponse.status);
        } catch (fallbackError) {
          logger.error('Fallback save also failed:', fallbackError);
        }
      }
      
      logger.debug('Backend save completed');
      
    } catch (apiError) {
      logger.error('Error in saveProcessDataToBackend:', apiError);
      // Don't set an error message since we've already saved to localStorage
      // This ensures API errors don't affect the user experience
    }
  };

  const renderEditableField = (field, label) => (
    <div className="min-h-[3rem]">
      {editing[field] ? (
        <textarea
          value={mindset[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="textarea textarea-bordered w-full min-h-[4rem] resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
          placeholder={`Enter your ${label.toLowerCase()}...`}
          rows={2}
        />
      ) : (
        <div className="prose max-w-none">
          {mindset[field] ? (
            <p className="text-base-content whitespace-pre-wrap leading-relaxed">
              {mindset[field]}
            </p>
          ) : (
            <div className="text-center py-4">
              <p className="text-base-content/50 text-sm italic">
                Click edit to add your {label.toLowerCase()}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderEditableArray = (field, label) => {
    return (
      <div className="min-h-[3rem]">
        {editing[field] ? (
          <div className="space-y-3">
            {Array.isArray(mindset[field]) && mindset[field].map((item, index) => (
              <div key={index} className="card bg-base-50 border border-base-200 hover:border-base-300 transition-colors duration-200">
                <div className="card-body p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-base-content/80">
                        {index === 0 ? `Main ${label}` : `${label} ${index + 1}`}
                      </span>
                      {index === 0 && (
                        <span className="badge badge-primary badge-xs">Required</span>
                      )}
                    </div>
                    {index > 0 && (
                      <button
                        onClick={() => handleRemove(field, index)}
                        className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10 transition-colors duration-200"
                        aria-label="Remove item"
                      >
                      </button>
                    )}
                  </div>
                  <textarea
                    value={item}
                    onChange={(e) => handleArrayChange(field, index, e.target.value)}
                    className="textarea textarea-bordered w-full min-h-[4.5rem] text-sm resize-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                    placeholder={index === 0 ? `Enter your main ${label.toLowerCase()}...` : `Enter ${label.toLowerCase()} ${index + 1}...`}
                    rows={2}
                  />
                </div>
              </div>
            ))}
            
            {/* Add Button */}
            {mindset[field] && mindset[field].length < 5 && (
              <button
                onClick={() => handleAdd(field)}
                className="btn btn-outline btn-sm w-full gap-2 hover:scale-[1.02] transition-all duration-200"
              >
                Add {label}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {Array.isArray(mindset[field]) && mindset[field].filter(item => item && item.trim()).length > 0 ? (
              mindset[field].filter(item => item && item.trim()).map((item, index) => (
                <div key={index} className="bg-base-50 p-4 rounded-lg border border-base-200">
                  <p className="text-base-content whitespace-pre-wrap leading-relaxed">
                    {item}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-base-content/50 text-sm italic">
                  Click edit to add {label.toLowerCase()}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-sm text-base-content/60">Loading your mindset...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <GuideModal guideKey="Mindset" />
      <div className="container px-4 pt-4" style={{ width: '1024px !important', maxWidth: '1024px !important', margin: '0 auto !important', boxSizing: 'border-box !important' }}>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-4">Mindset</h1>
        
        {/* Wizard Button */}
        <button
          className="btn btn-primary btn-wide gap-2 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          onClick={() => setProcessWizardOpen(true)}
          aria-label="Open Mindset Wizard"
        >
          Mindset Wizard
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-error shadow-sm mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      {message && (
        <div className="alert alert-success shadow-sm mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
          <span className="text-sm">{message}</span>
        </div>
      )}

      {/* Global Save Button */}
      {(editing.oneWordFocus || editing.keysToKeepItSimple || editing.affirmationsAndBeliefs || editing.selfTalk || editing.bodyLanguage) && (
        <div className="sticky top-4 z-10 mb-6">
          <div className="flex justify-center">
            <button
              onClick={saveMindset}
              disabled={saveInProgress}
              className="btn btn-primary btn-wide hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
            >
              {saveInProgress ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Saving...
                </>
              ) : 'Save All Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Content Sections */}
      <div className="space-y-6">
        {/* One Word Focus */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">One Word Focus</h3>
              <button
                onClick={() => setEditing({ ...editing, oneWordFocus: !editing.oneWordFocus })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.oneWordFocus ? 'Cancel editing' : 'Edit one word focus'}
              >
                {editing.oneWordFocus ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {renderEditableField('oneWordFocus', 'One Word Focus')}
          </div>
        </div>

        {/* Keys to Keep It Simple */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Keys to Keep It Simple</h3>
              <button
                onClick={() => setEditing({ ...editing, keysToKeepItSimple: !editing.keysToKeepItSimple })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.keysToKeepItSimple ? 'Cancel editing' : 'Edit keys'}
              >
                {editing.keysToKeepItSimple ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {renderEditableArray('keysToKeepItSimple', 'Key')}
          </div>
        </div>

        {/* Affirmations & Beliefs */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Affirmations & Beliefs</h3>
              <button
                onClick={() => setEditing({ ...editing, affirmationsAndBeliefs: !editing.affirmationsAndBeliefs })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.affirmationsAndBeliefs ? 'Cancel editing' : 'Edit affirmations'}
              >
                {editing.affirmationsAndBeliefs ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {renderEditableArray('affirmationsAndBeliefs', 'Affirmation')}
          </div>
        </div>

        {/* Self-Talk */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Self-Talk</h3>
              <button
                onClick={() => setEditing({ ...editing, selfTalk: !editing.selfTalk })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.selfTalk ? 'Cancel editing' : 'Edit self-talk'}
              >
                {editing.selfTalk ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {renderEditableArray('selfTalk', 'Self-Talk Phrase')}
          </div>
        </div>

        {/* Body Language */}
        <div className="card bg-base-100 shadow-md hover:shadow-lg transition-shadow duration-200 desktop-standard-card">
          <div className="card-body p-3 sm:p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary whitespace-nowrap">Body Language</h3>
              <button
                onClick={() => setEditing({ ...editing, bodyLanguage: !editing.bodyLanguage })}
                className="btn btn-ghost btn-sm btn-square hover:bg-base-200 transition-colors duration-200"
                aria-label={editing.bodyLanguage ? 'Cancel editing' : 'Edit body language'}
              >
                {editing.bodyLanguage ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {renderEditableArray('bodyLanguage', 'Body Language Cue')}
          </div>
        </div>

        {/* Bottom Action Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowVisualizationAudio(true)}
            className="btn btn-accent btn-wide hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            aria-label="Open Mindset Audio"
          >
            Open Mindset Audio
          </button>
        </div>

        {/* Modals */}
        <Modal
          isOpen={showVisualizationAudio}
          onClose={() => setShowVisualizationAudio(false)}
          title="Mindset Audio Creator"
        >
          <AudioVisualization 
            onComplete={() => setShowVisualizationAudio(false)}
          />
        </Modal>

        {processWizardOpen && (
          <ProcessWizard
            isOpen={processWizardOpen}
            onClose={() => setProcessWizardOpen(false)}
            onComplete={handleProcessWizardComplete}
          />
        )}
      </div>
      </div>
    </>
  );
});

export default Mindset;